import React, { useMemo } from 'react';
import styles from './PageReview.module.scss';
import { useAssessmentContext } from '../../../../../AssessmentContext';
import { useResponseContext } from 'js/engines/CapabilitiesAssessment/ResponseContext';
import {
  getPageCapabilities,
  getPageTitle,
  getQuestionId,
} from 'js/engines/CapabilitiesAssessment/utils';
import CapabilityReview from './CapabilityReview';
import { faSquare } from '@fortawesome/pro-solid-svg-icons';
import { Icon } from 'js/components';
import AdditionalElementReview from './AdditionalElementReview';
function CapabilitiesPageReview({ page }) {
  const { selectedCapabilities } = page;
  const { capabilitySets, answerSets } = useAssessmentContext();
  const { responses, questionMap } = useResponseContext();

  const capabilitySet = useMemo(
    () => capabilitySets[page.capabilitySetId] ?? { capabilities: [] },
    [capabilitySets, page.capabilitySetId]
  );

  const pageCapabilities = useMemo(() => {
    return getPageCapabilities(capabilitySet, selectedCapabilities);
  }, [capabilitySet, selectedCapabilities]);

  const capabilityResponses = useMemo(() => {
    return pageCapabilities.reduce((cAcc, capability) => {
      const capabilityResponses = page.capabilityElements.reduce(
        (elementAcc, element) => {
          const questionId = getQuestionId(element, capability, questionMap);
          const response = responses[questionId];
          const answerSet = answerSets[element.answerSetId];
          const score =
            response && !response.is_na
              ? answerSet.scale(+response.value)
              : undefined;
          elementAcc[element.guid] = {
            capability,
            answerSet: answerSets[element.answerSetId],
            response,
            score,
          };
          return elementAcc;
        },
        {}
      );
      cAcc[capability.id] = capabilityResponses;
      return cAcc;
    }, {});
  }, [
    pageCapabilities,
    answerSets,
    questionMap,
    responses,
    page.capabilityElements,
  ]);

  return (
    <div className={styles['page-review'] + ' question-results'}>
      <div className={styles['page-review-header']}>
        <h4 className={styles['page-review-title']}>
          {getPageTitle(page, capabilitySets)}
        </h4>
      </div>
      <div className={styles['legend']}>
        {page.capabilityElements.map((element, i) => (
          <div
            key={element.guid}
            className={styles['legend-item'] + ` palette-chart-${i + 2}`}
          >
            <Icon
              icon={faSquare}
              size="lg"
              className="palette-fill icon-left"
            />
            <span>{element.name}</span>
          </div>
        ))}
      </div>
      <div className={styles['page-review-body']}>
        <div className={styles['capability-responses']}>
          {pageCapabilities.map((capability) => (
            <CapabilityReview
              key={capability.guid}
              pageId={page.guid}
              capability={capability}
              capabilityElements={page.capabilityElements}
              responses={capabilityResponses[capability.id]}
            />
          ))}
        </div>
        {page.additionalElements.length > 0 && (
          <div className={styles['additional-elements']}>
            {page.capabilityElements.map((capabilityElement) => {
              return (
                <AdditionalElementReview
                  key={capabilityElement.guid}
                  page={page}
                  capabilityElement={capabilityElement}
                  questionMap={questionMap}
                  responses={responses}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default CapabilitiesPageReview;
